import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import AddIcon from "@mui/icons-material/Add";
import { QueryState } from "@/components/common/AsyncState";
import { StatusChip } from "@/components/common/StatusChip";
import { fetchBranches } from "@/api/branches";
import { createUser, fetchUsers } from "@/api/settings";
import { ROLE_LABELS, USER_STATUS_LABELS } from "@/types/enums";
import type { Role } from "@/types/enums";
import { formatDate } from "@/utils/format";

function UserCreateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: fetchBranches });
  const [role, setRole] = useState<Role>("ADMIN");
  const [fullName, setFullName] = useState("");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [branchId, setBranchId] = useState("");

  const mutation = useMutation({
    mutationFn: () => createUser({ role, fullName, login, password, phone: phone || undefined, branchId: branchId || undefined }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Новый пользователь</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField select label="Роль" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {Object.entries(ROLE_LABELS)
              .filter(([key]) => key !== "TEACHER" && key !== "STUDENT")
              .map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
          </TextField>
          <TextField label="ФИО" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          <TextField label="Телефон" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <TextField select label="Филиал (если применимо)" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            <MenuItem value="">Все филиалы</MenuItem>
            {(branches ?? []).map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Логин" value={login} onChange={(e) => setLogin(e.target.value)} />
          <TextField label="Пароль" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Отмена
        </Button>
        <Button variant="contained" disabled={!fullName || !login || !password || mutation.isPending} onClick={() => mutation.mutate()}>
          Создать
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function UsersTab() {
  const { data, isLoading, error } = useQuery({ queryKey: ["users"], queryFn: () => fetchUsers({ limit: 100 }) });
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Новый пользователь
        </Button>
      </Stack>
      <QueryState isLoading={isLoading} error={error}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ФИО</TableCell>
              <TableCell>Логин</TableCell>
              <TableCell>Роль</TableCell>
              <TableCell>Телефон</TableCell>
              <TableCell>Дата создания</TableCell>
              <TableCell>Статус</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.items ?? []).map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.fullName}</TableCell>
                <TableCell>{user.login}</TableCell>
                <TableCell>{ROLE_LABELS[user.role]}</TableCell>
                <TableCell>{user.phone ?? "—"}</TableCell>
                <TableCell>{formatDate(user.createdAt)}</TableCell>
                <TableCell>
                  <StatusChip label={USER_STATUS_LABELS[user.status]} />
                </TableCell>
              </TableRow>
            ))}
            {(data?.items ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Пользователи не найдены.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </QueryState>
      <UserCreateDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
}
